/**
 * CaseFilePage
 *
 * Main CaseFile module page with document management.
 * Based on CASEFILE_FUNCTIONAL_SPEC.md - Phase 1 implementation.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Sidebar } from '../components/dashboard';
import {
  CaseFileHeader,
  CareTeamModal,
  DocumentCategories,
  DocumentCategory,
  CategoryCount,
  defaultCategories,
  ExportDocumentsModal,
} from '../components/casefile';
import { DocumentsList, DocumentUpload } from '../components/documents';
import { LockedCaseFileBanner } from '../components/authorization';
import { DocumentSummary } from '../components/documents/DocumentCard';

type CaseFilePermission = 'owner' | 'editor' | 'logger' | 'viewer';

// ACL v2.2 relationship types
type CareTeamRelationship =
  // ACL v2.2 relationships (for owner assignment)
  | 'self'          // Care recipient's own account
  | 'parent'        // Parent of care recipient
  | 'child'         // Adult child of care recipient
  | 'spouse'        // Spouse/partner
  | 'sibling'       // Brother/sister
  | 'grandparent'   // Grandparent
  | 'extended'      // Aunt, uncle, cousin, etc.
  | 'other'         // Friend, neighbor, etc.
  // Legacy relationships
  | 'primary'
  | 'secondary'
  | 'medical'
  | 'educational'
  | 'coordinator'
  | 'professional';

// ACL v2.2 authority types
type CaseFileAuthority =
  | 'patient_self'
  | 'parent_of_minor'
  | 'legal_guardian'
  | 'designated_agent';

interface CareTeamMember {
  userId: string;
  relationship: CareTeamRelationship;
  permissions: CaseFilePermission;
  name?: string;
  email?: string;
  role?: string;
  addedAt?: { _seconds: number };
  addedBy?: string;
}

interface CaseFile {
  caseFileId: string;
  name: string;
  dateOfBirth: string;
  documentCount?: number;
  ownerId: string;
  careTeam?: CareTeamMember[];
  isArchived?: boolean;
  updatedAt?: { _seconds: number };
  // ACL v2.2 fields
  authorityType?: CaseFileAuthority;
  adultTransitionDate?: string;
  adultTransitionStatus?: 'upcoming' | 'pending_reauthorization' | 'reauthorized' | 'transferred';
  // v2.3 lock status
  isLocked?: boolean;
  lockReason?: string;
}

interface GetCaseFileResponse {
  success: boolean;
  caseFile: CaseFile;
  userPermissions: CaseFilePermission;
  isOwner: boolean;
}

export function CaseFilePage(): JSX.Element {
  const { caseFileId } = useParams<{ caseFileId: string }>();
  const navigate = useNavigate();

  // Data state
  const [caseFile, setCaseFile] = useState<CaseFile | null>(null);
  const [userPermissions, setUserPermissions] = useState<CaseFilePermission>('viewer');
  const [isOwner, setIsOwner] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<DocumentCategory>('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCareTeamModal, setShowCareTeamModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Category counts - calculated from loaded documents
  const [categoryCounts, setCategoryCounts] = useState<CategoryCount[]>(defaultCategories);

  // Calculate category counts when documents are loaded
  const handleDocumentsLoaded = useCallback((documents: DocumentSummary[]) => {
    // Count documents by category
    const counts: Record<string, number> = {};
    documents.forEach((doc) => {
      const category = doc.category || 'Other';
      counts[category] = (counts[category] || 0) + 1;
    });

    // Update category counts
    setCategoryCounts(
      defaultCategories.map((cat) => ({
        ...cat,
        count: cat.category === 'all' ? documents.length : (counts[cat.category] || 0),
      }))
    );
  }, []);

  const fetchCaseFile = async () => {
    if (!caseFileId) return;

    setLoading(true);
    setError(null);

    try {
      const getCaseFile = httpsCallable<{ caseFileId: string }, GetCaseFileResponse>(
        functions,
        'getCaseFile'
      );
      const result = await getCaseFile({ caseFileId });

      if (result.data.success) {
        setCaseFile(result.data.caseFile);
        setUserPermissions(result.data.userPermissions);
        setIsOwner(result.data.isOwner);
        // Category counts are now set via handleDocumentsLoaded callback
      } else {
        setError('Failed to load CaseFile');
      }
    } catch (err) {
      console.error('Error fetching CaseFile:', err);
      // Show user-friendly error messages without exposing internal IDs
      const rawMessage = err instanceof Error ? err.message : '';
      if (rawMessage.toLowerCase().includes('not found') || rawMessage.toLowerCase().includes('does not have access')) {
        setError('This care record could not be found. It may have been removed or you may not have access.');
      } else if (rawMessage.toLowerCase().includes('unauthenticated') || rawMessage.toLowerCase().includes('unauthorized')) {
        setError('Your session has expired. Please refresh the page and log in again.');
      } else {
        setError('Something went wrong while loading this care record. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseFile();
  }, [caseFileId]);

  const handleUploadClick = () => {
    // Don't allow uploads when CaseFile is locked
    if (caseFile?.isLocked) return;
    setShowUploadModal(true);
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    setRefreshKey((prev) => prev + 1);
    fetchCaseFile(); // Refresh document count
  };

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleEditClick = () => {
    navigate(`/casefile/${caseFileId}/edit`);
  };

  const handleArchiveClick = () => {
    // TODO: Archive/restore CaseFile
    console.log('Archive clicked');
  };

  const handleManageTeamClick = () => {
    setShowCareTeamModal(true);
  };

  const handleCareTeamUpdated = () => {
    // Refresh CaseFile data to get updated care team
    fetchCaseFile();
  };

  const handleCategoryChange = (category: DocumentCategory) => {
    setActiveCategory(category);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-airy flex">
        <div className="hidden lg:block">
          <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-evergreen border-t-transparent rounded-full mx-auto" />
            <p className="mt-4 text-muted">Loading CaseFile...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-airy flex">
        <div className="hidden lg:block">
          <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="card p-8 max-w-md text-center">
            <h2 className="text-lg font-semibold text-ink mb-2">Care Record Not Available</h2>
            <p className="text-muted mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <button onClick={fetchCaseFile} className="btn-secondary">
                Try Again
              </button>
              <button onClick={() => navigate('/')} className="btn-primary">
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!caseFile) {
    return (
      <div className="min-h-screen bg-airy flex">
        <div className="hidden lg:block">
          <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="card p-8 max-w-md text-center">
            <h2 className="text-lg font-semibold text-ink mb-2">Care Record Not Available</h2>
            <p className="text-muted mb-6">This care record could not be found. It may have been removed or you may not have access.</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Parse last updated date
  const lastUpdated = caseFile.updatedAt
    ? new Date(caseFile.updatedAt._seconds * 1000)
    : undefined;

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
      </div>

      {/* Sidebar - Mobile Overlay */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-ink/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar className="h-full" activeCaseFileId={caseFileId} />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* CaseFile Header */}
        <CaseFileHeader
          caseFileId={caseFileId!}
          name={caseFile.name}
          documentCount={caseFile.documentCount || 0}
          lastUpdated={lastUpdated}
          isArchived={caseFile.isArchived}
          careTeamCount={caseFile.careTeam?.length || 0}
          isOwner={isOwner}
          userPermission={userPermissions}
          isLocked={caseFile.isLocked}
          lockReason={caseFile.lockReason}
          onUploadClick={handleUploadClick}
          onExportClick={handleExportClick}
          onEditClick={handleEditClick}
          onArchiveClick={handleArchiveClick}
          onManageTeamClick={handleManageTeamClick}
        />

        {/* Locked CaseFile Banner - shown for all lock reasons */}
        {caseFile.isLocked && (
          <div className="px-4 lg:px-6 pt-4">
            <LockedCaseFileBanner
              caseFileId={caseFileId!}
              caseFileName={caseFile.name}
              lockReason={caseFile.lockReason}
              careRecipientName={caseFile.name}
              careRecipientDob={caseFile.dateOfBirth}
              onUnlocked={() => fetchCaseFile()}
            />
          </div>
        )}

        {/* Content Area: Categories Sidebar + Documents Grid */}
        <div className="flex-1 flex">
          {/* Document Categories Sidebar */}
          <div className="hidden md:block w-64 flex-shrink-0">
            <DocumentCategories
              counts={categoryCounts}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
              className="sticky top-0 h-[calc(100vh-80px)] overflow-y-auto"
            />
          </div>

          {/* Documents Area */}
          <div className="flex-1 p-4 lg:p-6 overflow-y-auto">
            {/* Mobile: Category dropdown */}
            <div className="md:hidden mb-4">
              <select
                value={activeCategory}
                onChange={(e) => handleCategoryChange(e.target.value as DocumentCategory)}
                className="input w-full"
              >
                {categoryCounts.map((cat) => (
                  <option key={cat.category} value={cat.category}>
                    {cat.label} ({cat.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Upload Area (shown when modal open or drag-drop zone) */}
            {showUploadModal && (
              <div className="mb-6">
                <div className="card">
                  <div className="card-header flex justify-between items-center">
                    <h3 className="font-semibold text-ink">Upload Documents</h3>
                    <button
                      onClick={() => setShowUploadModal(false)}
                      className="btn-ghost btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="card-body">
                    <DocumentUpload
                      caseFileId={caseFileId!}
                      onUploadComplete={handleUploadComplete}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Documents List */}
            <DocumentsList
              key={refreshKey}
              caseFileId={caseFileId!}
              viewMode="grid"
              categoryFilter={activeCategory}
              onDocumentsLoaded={handleDocumentsLoaded}
            />
          </div>
        </div>
      </div>

      {/* Care Team Modal */}
      <CareTeamModal
        isOpen={showCareTeamModal}
        onClose={() => setShowCareTeamModal(false)}
        caseFileId={caseFileId!}
        careRecipientName={caseFile.name}
        careTeam={caseFile.careTeam || []}
        ownerId={caseFile.ownerId}
        isLocked={caseFile.isLocked}
        lockReason={caseFile.lockReason}
        onCareTeamUpdated={handleCareTeamUpdated}
      />

      {/* Export Documents Modal */}
      <ExportDocumentsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        caseFileId={caseFileId!}
        caseFileName={caseFile.name}
      />

    </div>
  );
}

export default CaseFilePage;
